import { useMemo } from "react";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import { normalizeQueryResult } from "../utils/queryNormalization";
import type { EntityQueryResult } from "../types";

/**
 * Processes and filters entity query results
 */
export function useDataProcessing<T extends BaseEntity>(
  entitiesQueryRaw: any,
  searchTerm: string,
  tableFilters: Record<string, any>,
  config: SimpleTableConfig<T>,
  entityName: string
): EntityQueryResult<T> & { filteredData: T[] } {
  // Normalize the query result to EntityHooks shape (handle both old and new shapes)
  const entitiesQuery = useMemo(() => {
    return normalizeQueryResult<T>(entitiesQueryRaw);
  }, [entitiesQueryRaw]);

  const query = useMemo(() => {
    return {
      data: entitiesQuery.data,
      isLoading: entitiesQuery.isLoading,
      error: entitiesQuery.error,
      refetch: entitiesQuery.refetch,
    };
  }, [entitiesQuery]);

  // Use the query data directly
  const data = query.data;

  // Use query loading state directly
  const isLoading = query.isLoading;
  // Bypass errors for now to prevent blocking the UI
  // Log errors but don't prevent the page from rendering
  const error = query.error;
  if (error && process.env.NODE_ENV === "development") {
    console.warn("GraphQL Query Error (bypassed):", error);
  }

  const refetch = query.refetch;

  // Apply filtering before grouping to ensure grouping works on filtered results
  // NOTE: All filtering is now done server-side via GraphQL
  // Client-side filtering has been removed to ensure consistent results
  const filteredData = useMemo(() => {
    const filtered = data?.data || [];

    // Data is already filtered by GraphQL query, no client-side filtering needed
    // Search and all filters are applied server-side via useQueryParams

    return filtered;
  }, [data?.data]);

  return useMemo(
    () => ({
      ...entitiesQuery,
      filteredData: filteredData,
    }),
    [entitiesQuery, filteredData]
  );
}
